import * as React from 'react';
import { isDefined } from 'react-financial-charts';
import { HoverTextNearMouse, MouseLocationIndicator } from '@react-financial-charts/interactive/lib/components/index';
import { isHoverForInteractiveType, saveNodeType, terminate } from '@react-financial-charts/interactive/lib/utils';
import { EachGannFan } from './Wrapper';
import type { GannFanDragData } from './Wrapper/EachGannFan';
import { ShapeType } from '@/types/commontypes';
import type { ChartId, RenderNode } from '../../..';
import type { DragOverrideBase, GannBoxRenderNode, GannFanRenderNode } from '../../../types/commontypes';
import type { DrawingSharedState, HoverTextConfig, MoreProps } from '../../../types/commontypes';
import { clampYToChart } from '../../..';
import { toGannboxRFCProps, toGannFanRFCProps } from '@/utils/shapePropsToAppearance';
import { getActiveTemplateNode } from '@/utils/useActiveShapeProps';
import { defaultShapePropertyValues } from '@/utils/defaultShapePropertyValues';
import { chartOrder } from '@/utils/chartConfig';
import { ChartShapesContext } from '../../../contexts/ChartShapesContext';

export interface GannFanProps {
  readonly enabled: boolean;
  readonly onStart: (e: React.MouseEvent, xyValue: [number, number], moreProps: MoreProps) => void;
  readonly onComplete: (e: React.MouseEvent, newfans: RenderNode[], moreProps: MoreProps) => void;
  readonly onDragComplete: (e: React.MouseEvent, newTrends: RenderNode[], moreProps: MoreProps) => void;
  readonly onSelect?: (e: React.MouseEvent, interactives: RenderNode[], moreProps: MoreProps) => void;
  readonly currentPositionStroke?: string;
  readonly currentPositionStrokeWidth?: number;
  readonly currentPositionOpacity?: number;
  readonly currentPositionRadius?: number;
  hoverText?: Partial<HoverTextConfig>;
  readonly fans: RenderNode[];
  readonly isGannBox?: boolean;
  readonly hoverId: string | null;
  readonly onHover?: (hoveredId: string | number | null, moreProps?: MoreProps) => void;
  readonly onDoubleClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly chartId?: ChartId;
  readonly sharedState?: DrawingSharedState;
  readonly setSharedState?: React.Dispatch<React.SetStateAction<DrawingSharedState>>;
  readonly type: ShapeType;
  readonly isAnyShapeEnabled?: boolean;
}

interface GannFanDragOverride extends DragOverrideBase, GannFanDragData {}

interface GannFanState {
  fan?: RenderNode | RenderNode[] | null;
  override?: GannFanDragOverride;
}

export class GannFan extends React.Component<GannFanProps, GannFanState> {
  public static contextType = ChartShapesContext;
  declare context: React.ContextType<typeof ChartShapesContext>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static _cachedDefaultProps: any = null;
  static get defaultProps() {
    if (!GannFan._cachedDefaultProps) {
      GannFan._cachedDefaultProps = {
        currentPositionStroke: '#1e53e6',
        currentPositionOpacity: 1,
        currentPositionStrokeWidth: 1,
        currentPositionRadius: 4,
        hoverText: {
          ...HoverTextNearMouse.defaultProps,
          enable: true,
          fontFamily: 'Helvetica',
          fontSize: 12,
          fill: '#000',
          bgFill: '#FFFFFF',
          bgOpacity: 0.85,
          bgWidth: 90,
          bgHeight: 40,
          text: 'Click to select object',
        },
        fans: [],
        isGannBox: false,
      };
    }
    return GannFan._cachedDefaultProps;
  }

  private mouseMoved = false;

  private getSelectionState: (...args: unknown[]) => unknown;
  private saveNodeType: <T>(idx: number) => React.Ref<T>;
  private terminate: (...args: unknown[]) => unknown;

  public constructor(props: GannFanProps) {
    super(props);

    this.terminate = terminate.bind(this);
    this.saveNodeType = saveNodeType.bind(this);

    this.getSelectionState = isHoverForInteractiveType('fans').bind(this);

    this.state = {};
  }

  public render() {
    const {
      currentPositionOpacity,
      currentPositionRadius = GannFan.defaultProps.currentPositionRadius,
      currentPositionStroke,
      currentPositionStrokeWidth,
      enabled,
      fans,
      hoverText,
      isGannBox,
      onDoubleClick,
      sharedState,
      chartId,
      type,
      isAnyShapeEnabled,
    } = this.props;

    const isBox = type === ShapeType.GANN_BOX;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const activeNode = getActiveTemplateNode(type) as GannFanRenderNode | GannBoxRenderNode;
    const appearance = isBox
      ? // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        (toGannboxRFCProps(activeNode as GannBoxRenderNode) as unknown as ReturnType<typeof toGannFanRFCProps>)
      : toGannFanRFCProps(activeNode as GannFanRenderNode);

    const { override } = this.state;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion, @typescript-eslint/consistent-type-assertions
    const hoverTextWithDefault = {
      ...GannFan.defaultProps.hoverText,
      ...hoverText,
    } as HoverTextConfig;

    const tempCondition =
      sharedState?.type === type &&
      chartId === sharedState?.chartId &&
      sharedState?.endXY &&
      sharedState?.status !== 'completed';

    const tempChannel = tempCondition ? (
      <EachGannFan
        uuid={''}
        interactive={false}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        startXY={sharedState?.startXY as [number, number]}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        endXY={sharedState?.endXY as [number, number]}
        appearance={appearance}
        hoverText={hoverTextWithDefault}
        isGannBox={isGannBox}
        timeLevels={appearance.timeLevels}
        priceLevels={appearance.priceLevels}
        selected={true}
      />
    ) : null;
    return (
      <g>
        {fans.map((each, idx) => {
          const eachAppearance = isBox
            ? // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              (toGannboxRFCProps(each as GannBoxRenderNode) as unknown as ReturnType<typeof toGannFanRFCProps>)
            : // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              toGannFanRFCProps(each as GannFanRenderNode);
          if (each.hidden) return null;
          const mergedProps = each?.uuid === override?.uuid ? { ...each, ...override } : each;
          return (
            <EachGannFan
              key={idx}
              ref={this.saveNodeType(idx)}
              index={idx}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              {...(mergedProps as unknown as React.ComponentProps<typeof EachGannFan>)}
              uuid={each.uuid}
              selected={(each as unknown as { selected?: boolean }).selected}
              appearance={eachAppearance}
              timeLevels={eachAppearance.timeLevels}
              priceLevels={eachAppearance.priceLevels}
              hoverText={hoverTextWithDefault}
              onDrag={this.handleDragFan}
              onDragComplete={this.handleDragFanComplete}
              isGannBox={isGannBox}
              onSelect={this.handleSelect}
              // onDragStart={(id: number) => this.setState({ override: { id } })}
              onHover={this.handleHover}
              locked={each.locked ?? false}
              onDoubleClick={onDoubleClick}
              sharedState={sharedState}
              isAnyShapeEnabled={isAnyShapeEnabled}
            />
          );
        })}
        {tempChannel}
        <MouseLocationIndicator
          enabled={enabled}
          snap={false}
          r={currentPositionRadius}
          stroke={currentPositionStroke}
          opacity={currentPositionOpacity}
          strokeWidth={currentPositionStrokeWidth}
          onMouseDown={
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            this.handleStart as unknown as (e: React.MouseEvent, xyValue: number[], moreProps: MoreProps) => void
          }
          onMouseMove={
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            this.handleDrawFan as unknown as (e: React.MouseEvent, xyValue: number[], moreProps: MoreProps) => void
          }
        />
      </g>
    );
  }

  private readonly handleHover = (hoveredId: string | number | null, moreProps?: MoreProps) => {
    const { onHover } = this.props;
    if (onHover) {
      onHover(hoveredId, moreProps);
    }
  };

  private readonly handleSelect = (e: React.MouseEvent, selectedUuid: string, moreProps: MoreProps) => {
    const { fans } = this.props;

    const newFans = fans.map(fan => ({
      ...fan,
      selected: fan.uuid === selectedUuid,
    }));

    const { onSelect } = this.props;
    if (onSelect) {
      onSelect(e, newFans, moreProps);
    }
  };

  private readonly handleStart = (e: React.MouseEvent, xyValue: [number, number], moreProps: MoreProps) => {
    const { onStart, sharedState, enabled } = this.props;
    if (!enabled) return;
    if (sharedState?.status !== 'started') {
      // First mouse down — start drawing
      this.mouseMoved = false;
      if (onStart) onStart(e, xyValue, moreProps);
    } else {
      // Second mouse down — finalize the shape
      const shapesChartId = sharedState?.chartId;
      // Only do min/max clamp if sharedState exists
      const yValue = clampYToChart(moreProps.chartId, shapesChartId, moreProps.chartConfigs, chartOrder, xyValue[1]);
      this.handleEnd(e, [xyValue[0], yValue], moreProps);
    }
  };

  private readonly handleEnd = (e: React.MouseEvent, xyValue: [number, number], moreProps: MoreProps) => {
    const { fans, sharedState, enabled } = this.props;
    const chartId = moreProps.chartId;
    if (!enabled) return;
    if (sharedState?.status === 'started' && sharedState?.startXY) {
      const targetChartId = sharedState?.chartId || chartId;
      const shapeUuid = crypto.randomUUID();
      const creationShapeType: ShapeType = this.props.type ?? ShapeType.GANN_FAN;
      const lastProps = getActiveTemplateNode(creationShapeType);
      const defaults = defaultShapePropertyValues[creationShapeType];
      const initial = { ...defaults, ...lastProps };
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const newfans = [
        ...fans.map(d => ({ ...d, selected: false })),
        {
          ...initial,
          startXY: sharedState?.startXY,
          endXY: xyValue,
          selected: true,
          hidden: false,
          type: creationShapeType,
          chartId: targetChartId,
          uuid: shapeUuid,
          createdAt: Date.now(),
          updatedAt: Date.now(),
        },
      ] as RenderNode[];
      this.setState(
        {
          fan: newfans,
        },
        () => {
          const { onComplete } = this.props;
          if (onComplete) {
            onComplete(e, newfans, moreProps);
          }
        },
      );
    }
  };

  private readonly handleDrawFan = (_: React.MouseEvent, xyValue: [number, number], moreProps: MoreProps) => {
    const { setSharedState, sharedState, chartId, enabled } = this.props;
    if (!sharedState || sharedState.status !== 'started' || !enabled) return;

    const startChartId = sharedState.chartId;
    const pointerChartId = moreProps.currentCharts?.[0];

    // Only draw for shapes created on this chart
    if (chartId !== startChartId) return;

    this.mouseMoved = true;
    const [x] = xyValue;
    let [, y] = xyValue;

    // Clamp Y if pointer is on a different chart
    y = clampYToChart(pointerChartId, startChartId, moreProps.chartConfigs, chartOrder, y);

    setSharedState?.({
      ...sharedState,
      endXY: [x, y],
    });
  };

  private readonly handleDragFanComplete = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { override } = this.state;
    const { fans, onDragComplete, hoverId } = this.props;

    if (isDefined(override) && override.uuid === hoverId) {
      const { uuid, ...rest } = override;
      if (!isDefined(rest.startXY) || !isDefined(rest.endXY)) {
        return;
      }
      const newFans = fans.map(each =>
        each.uuid === uuid ? { ...each, ...rest, selected: true, updatedAt: Date.now() } : { ...each, selected: false },
      );

      onDragComplete(e, newFans, moreProps);
      this.setState({ override: undefined });
    }
  };

  private readonly handleDragFan = (_: React.MouseEvent, uuid: string, newXYValue: GannFanDragData) => {
    const { hoverId } = this.props;
    const { override } = this.state;
    if (override) {
      if (override.uuid !== uuid || override.uuid !== hoverId) {
        return;
      }
    }
    this.setState({
      override: {
        uuid,
        ...newXYValue,
      },
    });
  };
}
